/**
 * Report Management Service
 * Handles saved reports, metadata, archiving, and sharing
 */

import {
  collection,
  doc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  Timestamp,
} from 'firebase/firestore';
import { db, auth } from './firebase';
import { FirestoreService, COLLECTIONS } from './firestore';
import { logger } from '@/utils/logger';
import { ReportData } from '@/types/reports';

export interface SavedReport {
  id: string;
  title: string;
  description?: string;
  organizationId: string;
  organizationName: string;
  calculationId: string;
  reportData: ReportData;
  tags: string[];
  status: 'draft' | 'final' | 'archived';
  isShared: boolean;
  sharedWith: string[];
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
  lastAccessedAt?: Date;
  accessCount: number;
  metadata: {
    totalResidents: number;
    totalStaff: number;
    totalCost: number;
    reportType: 'standard' | 'comparison' | 'projection';
    version: string;
  };
}

export interface ReportFilter {
  organizationId?: string;
  status?: SavedReport['status'];
  tags?: string[];
  dateRange?: {
    start: Date;
    end: Date;
  };
  createdBy?: string;
  search?: string;
}

export interface ReportListOptions {
  filter?: ReportFilter;
  sortBy?: 'createdAt' | 'updatedAt' | 'title' | 'accessCount';
  sortOrder?: 'asc' | 'desc';
  limit?: number;
  startAfter?: any;
}

export class ReportManagementService extends FirestoreService<SavedReport> {
  constructor() {
    super(COLLECTIONS.REPORTS);
  }

  /**
   * Save a new report
   */
  async saveReport(
    reportData: ReportData,
    calculationId: string,
    organizationId: string,
    organizationName: string,
    userId: string,
    options: {
      title?: string;
      description?: string;
      tags?: string[];
      status?: SavedReport['status'];
    } = {}
  ): Promise<string> {
    const reportId = doc(collection(db, COLLECTIONS.REPORTS)).id;

    const savedReport: Omit<SavedReport, 'id' | 'createdAt' | 'updatedAt'> = {
      title:
        options.title ||
        `Reporte ${organizationName} - ${new Date().toLocaleDateString('es-CR')}`,
      description: options.description || '',
      organizationId,
      organizationName,
      calculationId,
      reportData,
      tags: options.tags || [],
      status: options.status || 'draft',
      isShared: false,
      sharedWith: [],
      createdBy: userId,
      accessCount: 0,
      metadata: {
        totalResidents: reportData.calculationInputs?.totalResidents || 0,
        totalStaff: reportData.results?.staffing?.totalStaff || 0,
        totalCost: reportData.results?.costs?.baseCosts?.totalCost || 0,
        reportType: reportData.comparison ? 'comparison' : 'standard',
        version: reportData.version || '1.0.0',
      },
    };

    await this.create(reportId, {
      ...savedReport,
      createdAt: new Date(),
      updatedAt: new Date(),
    });
    return reportId;
  }

  /**
   * Get reports with filtering and pagination
   */
  async getReports(options: ReportListOptions = {}): Promise<SavedReport[]> {
    const baseQuery = collection(db, COLLECTIONS.REPORTS);
    const constraints: any[] = [];

    // Apply filters
    if (options.filter) {
      const { organizationId, status, tags, dateRange, createdBy } =
        options.filter;

      if (organizationId) {
        constraints.push(where('organizationId', '==', organizationId));
      }

      if (status) {
        constraints.push(where('status', '==', status));
      }

      if (createdBy) {
        constraints.push(where('createdBy', '==', createdBy));
      }

      if (tags && tags.length > 0) {
        constraints.push(where('tags', 'array-contains-any', tags));
      }

      if (dateRange) {
        constraints.push(
          where('createdAt', '>=', Timestamp.fromDate(dateRange.start))
        );
        constraints.push(
          where('createdAt', '<=', Timestamp.fromDate(dateRange.end))
        );
      }
    }

    // Apply sorting
    const sortField = options.sortBy || 'createdAt';
    const sortDirection = options.sortOrder || 'desc';
    constraints.push(orderBy(sortField, sortDirection));

    // Apply limit
    if (options.limit) {
      constraints.push(limit(options.limit));
    }

    // Apply cursor pagination
    if (options.startAfter) {
      constraints.push(startAfter(options.startAfter));
    }

    const q = query(baseQuery, ...constraints);
    const querySnapshot = await getDocs(q);

    let reports = querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate(),
        updatedAt: data.updatedAt?.toDate(),
        lastAccessedAt: data.lastAccessedAt?.toDate(),
      } as SavedReport;
    });

    // Apply text search filter (client-side for now)
    if (options.filter?.search) {
      const searchTerm = options.filter.search.toLowerCase();
      reports = reports.filter(
        report =>
          report.title.toLowerCase().includes(searchTerm) ||
          report.description?.toLowerCase().includes(searchTerm) ||
          report.organizationName.toLowerCase().includes(searchTerm) ||
          report.tags.some(tag => tag.toLowerCase().includes(searchTerm))
      );
    }

    return reports;
  }

  /**
   * Get report by ID and update access tracking
   */
  async getReportById(id: string): Promise<SavedReport | null> {
    const report = await super.getById(id);

    if (report) {
      // Update access tracking
      await this.update(id, {
        lastAccessedAt: new Date(),
        accessCount: (report.accessCount || 0) + 1,
      } as Partial<SavedReport>);
    }

    return report;
  }

  /**
   * Update report metadata and content
   */
  async updateReport(
    id: string,
    updates: {
      title?: string;
      description?: string;
      tags?: string[];
      status?: SavedReport['status'];
      reportData?: ReportData;
    }
  ): Promise<void> {
    const updateData: Partial<SavedReport> = {
      ...updates,
    };

    if (updates.reportData) {
      updateData.metadata = {
        totalResidents: updates.reportData.calculationInputs.totalResidents,
        totalStaff: updates.reportData.results.staffing.totalStaff,
        totalCost: updates.reportData.results.costs.baseCosts.totalCost || 0,
        reportType: updates.reportData.comparison ? 'comparison' : 'standard',
        version: updates.reportData.version,
      };
    }

    await this.update(id, updateData);
  }

  /**
   * Archive a report
   */
  async archiveReport(id: string): Promise<void> {
    await this.update(id, { status: 'archived' } as Partial<SavedReport>);
  }

  /**
   * Share a report with specific users
   */
  async shareReport(id: string, userIds: string[]): Promise<void> {
    await this.update(id, {
      isShared: true,
      sharedWith: userIds,
    } as Partial<SavedReport>);
  }

  /**
   * Unshare a report
   */
  async unshareReport(id: string): Promise<void> {
    await this.update(id, {
      isShared: false,
      sharedWith: [],
    } as Partial<SavedReport>);
  }

  /**
   * Get all unique tags
   */
  async getAllTags(): Promise<string[]> {
    const reports = await this.getReports({ limit: 1000 });
    const allTags = reports.flatMap(report => report.tags);
    return [...new Set(allTags)].sort();
  }

  /**
   * Get report statistics
   */
  async getReportStats(): Promise<{
    total: number;
    byStatus: Record<SavedReport['status'], number>;
    byOrganization: Record<string, number>;
    recentActivity: number;
  }> {
    logger.info('ReportManagement: Starting getReportStats', {
      firebaseUser: auth.currentUser
        ? {
            uid: auth.currentUser.uid,
            email: auth.currentUser.email,
          }
        : null,
    });

    try {
      const reports = await this.getReports({ limit: 1000 });
      logger.info('ReportManagement: Retrieved reports for stats', {
        count: reports.length,
      });

      const total = reports.length;
      const byStatus = reports.reduce(
        (acc, report) => {
          acc[report.status] = (acc[report.status] || 0) + 1;
          return acc;
        },
        {} as Record<SavedReport['status'], number>
      );

      const byOrganization = reports.reduce(
        (acc, report) => {
          acc[report.organizationName] =
            (acc[report.organizationName] || 0) + 1;
          return acc;
        },
        {} as Record<string, number>
      );

      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const recentActivity = reports.filter(
        report => report.createdAt > thirtyDaysAgo
      ).length;

      const stats = {
        total,
        byStatus,
        byOrganization,
        recentActivity,
      };

      logger.info('ReportManagement: Successfully calculated stats', stats);
      return stats;
    } catch (error) {
      logger.error('ReportManagement: Error getting report stats', {
        error: error instanceof Error ? error.message : String(error),
        firebaseUser: auth.currentUser ? { uid: auth.currentUser.uid } : null,
        stack: error instanceof Error ? error.stack : undefined,
      });
      throw error;
    }
  }

  /**
   * Duplicate a report
   */
  async duplicateReport(
    id: string,
    userId: string,
    newTitle?: string
  ): Promise<string> {
    const originalReport = await this.getReportById(id);

    if (!originalReport) {
      throw new Error('Report not found');
    }

    return this.saveReport(
      originalReport.reportData,
      originalReport.calculationId,
      originalReport.organizationId,
      originalReport.organizationName,
      userId,
      {
        title: newTitle || `Copia de ${originalReport.title}`,
        description: originalReport.description,
        tags: [...originalReport.tags],
        status: 'draft',
      }
    );
  }
}

export const reportManagementService = new ReportManagementService();
